import { t } from "@lingui/macro";
import graphql from "babel-plugin-relay/macro";
import { useAtomValue } from "jotai";
import { useFragment } from "react-relay";

import { Avatar } from "@whoosh/design-system/src/components/display";
import { HStack, Text } from "@whoosh/design-system/src/components/primitive";

import { PlayersSelectorAtom } from "../../../../atoms/selectors/playersSelectorAtom/playersSelectorAtom";
import { BookingWizardDataAtom } from "../../../../atoms/stores";
import { AddPlayersHeaderNode$key } from "./__generated__/AddPlayersHeaderNode.graphql";
import { getOtherPlayerCapacity } from "./utils/getOtherPlayerCapacity";

type Props = {
  nodeRef: AddPlayersHeaderNode$key | null;
  isGolfCourse: boolean;
};

export const AddPlayersHeader = ({ nodeRef, isGolfCourse }: Props) => {
  const node = useFragment(AddPlayersHeaderNodeFragmentString, nodeRef);
  const bookingWizardData = useAtomValue(BookingWizardDataAtom);
  const groupSize =
    bookingWizardData?.type === "RequestBooking"
      ? bookingWizardData?.capacity || 0
      : bookingWizardData?.groupSize || 1;

  const players = useAtomValue(PlayersSelectorAtom);

  const otherPlayerCapacity = getOtherPlayerCapacity({
    nodeRequirePlayerCounts: node?.requirePlayerCounts,
    isGolfCourse,
    bookingType: bookingWizardData?.type,
    nodeStandardCapacity: node?.standardCapacity,
    nodeUsedCapacity: node?.usedCapacity,
    groupSize,
    playersLength: players.length,
  });

  const isLesson = bookingWizardData?.type === "LessonBooking";

  // Determine if we should show a condensed view of available players
  const showCondensedView = groupSize > 6 && node?.requirePlayerCounts;

  return (
    <HStack
      px={5}
      py={4}
      alignItems="center"
      justifyContent="flex-start"
      gap={2}
      borderBottomWidth={1}
      borderColor="outline-default"
      bg="surface-primary"
      flexWrap="wrap"
    >
      {showCondensedView ? (
        <HStack alignItems="center" gap={2}>
          <Avatar size={"sm"} type={"unknownCustomer"} />
          <Text variant="mainBaseSemiBold">
            {otherPlayerCapacity}{" "}
            {otherPlayerCapacity === 1 ? t`Available spot` : t`Available spots`}
          </Text>
        </HStack>
      ) : (
        <>
          {players.map((player) => (
            <Avatar
              key={player.id}
              profileImagePublicId={
                player.clientType !== "unknownGuest" &&
                player.clientType !== "unknownCustomer"
                  ? player.profileImagePublicId ?? undefined
                  : undefined
              }
              firstName={player.firstName ?? null}
              lastName={player.lastName ?? null}
              size={"sm"}
              type={player.clientType}
            />
          ))}
          {Array.from({ length: otherPlayerCapacity }).map((_, index) => (
            <Avatar
              key={index}
              size={"sm"}
              type={
                isGolfCourse ||
                bookingWizardData?.type === "RequestBooking" ||
                isLesson
                  ? "empty"
                  : "member"
              }
            />
          ))}
        </>
      )}
    </HStack>
  );
};

export const AddPlayersHeaderNodeFragmentString = graphql`
  fragment AddPlayersHeaderNode on Node {
    ... on TimeSlot {
      requirePlayerCounts
      standardCapacity: capacity(bookingType: STANDARD)
      usedCapacity
    }
  }
`;
